import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { CheckCircle2, XCircle, Loader2, RotateCcw } from "lucide-react";

/**
 * Landing page reached via Moyasar's `callback_url` redirect after the user
 * completes / cancels a payment. Moyasar appends `id`, `status`, and
 * `message` query params.
 *
 * We call the backend `/payments/moyasar/completed` to authoritatively
 * verify + persist status (webhook is the primary source of truth, but this
 * gives immediate UI feedback).
 */
export default function PaymentResultPage() {
  const { lang } = useI18n();
  const [sp] = useSearchParams();
  const providerPaymentId = sp.get("id");
  const status = sp.get("status") || "";
  const message = sp.get("message");
  const paymentId = sp.get("cartal_payment_id");  // optional, if we later attach

  const [busy, setBusy] = useState(true);
  const [verifiedStatus, setVerifiedStatus] = useState(null);
  const [record, setRecord] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      // iter103 fix: always call /completed if we have the Moyasar id;
      // the backend now resolves our internal payment id automatically
      // (via provider_payment_id lookup or Moyasar metadata). Previously
      // this only fired when BOTH ids were in the URL, which Moyasar
      // sometimes strips — so paid invoices were never marked paid and
      // boost / subscription fulfillment never ran.
      if (providerPaymentId) {
        try {
          const r = await apiClient.post("/payments/moyasar/completed", {
            payment_id: paymentId || null,
            provider_payment_id: providerPaymentId,
          });
          if (alive) setVerifiedStatus(r.data.status);
        } catch (e) {
          // Non-fatal — the server-to-server webhook (`invoice-callback`)
          // will still reconcile eventually. Just log for diagnostics.
          console.warn("[PaymentResult] verify failed:", e);
        }

        // Optional admin lookup for a nicer receipt view.
        try {
          const list = await apiClient.get("/admin/payments", {
            params: { q: providerPaymentId, limit: 1 },
          });
          if (alive && list.data.items?.[0]) setRecord(list.data.items[0]);
        } catch { /* not admin */ }
      }
      if (alive) setBusy(false);
    })();
    return () => { alive = false; };
  }, [paymentId, providerPaymentId]);

  const finalStatus = verifiedStatus || status;
  const isPaid = finalStatus === "paid" || finalStatus === "succeeded";
  const isHeld = finalStatus === "held" || finalStatus === "authorized";
  const isFailed = finalStatus === "failed" || finalStatus === "canceled";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16" data-testid="payment-result-page">
      <div className="rounded-3xl border-2 p-8 text-center"
           style={{
             borderColor: isPaid ? "#22c55e" : isHeld ? "#c9a55c" : isFailed ? "#ef4444" : "#c9a55c",
             backgroundColor: "#ffffff",
           }}>
        {busy ? (
          <div className="flex flex-col items-center gap-3 py-8 text-[#6b6b6b]">
            <Loader2 className="w-8 h-8 animate-spin" />
            <p className="text-sm">{lang === "ar" ? "جاري تأكيد الدفع…" : "Confirming your payment…"}</p>
          </div>
        ) : isHeld ? (
          <div data-testid="payment-result-held">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                 style={{ background: "linear-gradient(135deg,#fbf6e8,#f5efe0)" }}>
              <CheckCircle2 className="w-8 h-8" style={{ color: "#8a6f2e" }} />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-[#0a0a0a] mb-2">
              {lang === "ar" ? "تم حجز المبلغ بنجاح 🛡️" : "Funds held successfully 🛡️"}
            </h1>
            <p className="text-sm text-[#525252] leading-relaxed mb-4">
              {lang === "ar"
                ? "تم تفعيل ضمان كارتال وحجز المبلغ على بطاقتك. لن يُخصم من رصيدك حتى تؤكد استلامك للسيارة من صفحة «دفعاتي» — أو يمكنك إلغاء الحجز مجاناً في أي وقت قبل التأكيد."
                : "CARTAL Guarantee is now active — the amount is held (not yet charged) on your card. It will only be captured after you confirm receipt from your Payment History page, or released back if you cancel the hold before confirmation."}
            </p>
            {providerPaymentId && (
              <div className="text-[11px] font-mono text-[#8a8a8a] mb-6 select-all" dir="ltr">
                Moyasar ID: {providerPaymentId}
              </div>
            )}
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/payments/history"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a]"
                    data-testid="result-go-history">
                {lang === "ar" ? "دفعاتي" : "My payments"}
              </Link>
              <Link to="/dashboard"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold border-2 border-[#c9a55c] text-[#8a6f2e] hover:bg-[#f5f0e6]">
                {lang === "ar" ? "لوحة التحكم" : "Dashboard"}
              </Link>
            </div>
          </div>
        ) : isPaid ? (
          <div data-testid="payment-result-success">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-[#0a0a0a] mb-2">
              {lang === "ar" ? "تم الدفع بنجاح 🎉" : "Payment successful 🎉"}
            </h1>
            <p className="text-sm text-[#525252] leading-relaxed mb-6">
              {lang === "ar"
                ? "تم تأكيد دفعتك وتفعيل الخدمة تلقائياً. سيصلك إشعار في حسابك."
                : "Your payment is confirmed and the service is activated. A notification has been sent to your account."}
            </p>
            {providerPaymentId && (
              <div className="text-[11px] font-mono text-[#8a8a8a] mb-6 select-all" dir="ltr">
                Moyasar ID: {providerPaymentId}
              </div>
            )}
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/dashboard"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a]"
                    data-testid="result-go-dashboard">
                {lang === "ar" ? "لوحة التحكم" : "Dashboard"}
              </Link>
              <Link to="/marketplace"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold border-2 border-[#c9a55c] text-[#8a6f2e] hover:bg-[#f5f0e6]">
                {lang === "ar" ? "متابعة التسوّق" : "Continue browsing"}
              </Link>
            </div>
          </div>
        ) : isFailed ? (
          <div data-testid="payment-result-failed">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-[#0a0a0a] mb-2">
              {lang === "ar" ? "لم يتم الدفع" : "Payment not completed"}
            </h1>
            <p className="text-sm text-[#525252] leading-relaxed mb-6">
              {message ? decodeURIComponent(message) : (lang === "ar"
                ? "حدث خطأ أثناء معالجة الدفع. لم يتم سحب أي مبلغ."
                : "Something went wrong while processing your payment. No funds were charged.")}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button type="button" onClick={() => window.history.back()}
                      className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a]"
                      data-testid="result-retry">
                <RotateCcw className="w-4 h-4" />
                {lang === "ar" ? "إعادة المحاولة" : "Try again"}
              </button>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold border-2 border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#f5f0e6]">
                {lang === "ar" ? "تواصل مع الدعم" : "Contact support"}
              </Link>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-[#525252]">
              {lang === "ar"
                ? "حالة الدفع قيد المعالجة — سيتم تحديث حسابك خلال دقائق."
                : "Payment status is pending — your account will update within minutes."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
